import PatientService from '@/services/PatientService'
import { displayMode } from '@/utils/displayMode'
import { logDev } from '@/utils/env'

/** Wat de installatie-prompt heeft opgeleverd. Alleen Chromium vuurt hier
 *  events voor: iOS meldt een installatie nooit. */
export type InstallEvent = 'prompt_seen' | 'install_accepted'

/** Eén melding per app-sessie. `getInfo` gaat bij elke navigatie over de lijn,
 *  daar willen we juist niet op meeliften. */
const SESSION_KEY = 'tmc_app_usage_reported'

/** De prompt staat op de loginpagina, dus op dat moment is er nog geen sessie
 *  om het aan te hangen. Bewaren tot de eerstvolgende ingelogde start. */
const PENDING_KEY = 'tmc_app_usage_pending'

function readStore(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeStore(store: Storage | undefined, key: string, value: string): void {
  try {
    store?.setItem(key, value)
  } catch {
    /* private mode, geblokkeerde site-data: dan meten we deze keer niet */
  }
}

function session(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.sessionStorage
}

function local(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.localStorage
}

function readPending(): InstallEvent[] {
  const raw = readStore(local(), PENDING_KEY)
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (e): e is InstallEvent => e === 'prompt_seen' || e === 'install_accepted'
    )
  } catch {
    return []
  }
}

function writePending(events: InstallEvent[]): void {
  writeStore(local(), PENDING_KEY, JSON.stringify(events))
}

/** Vastleggen dat de prompt is gezien of geaccepteerd. Gaat de wachtrij in,
 *  want de patient is op dat moment nog niet ingelogd. */
export function queueInstallEvent(event: InstallEvent): void {
  const pending = readPending()
  if (pending.includes(event)) return
  writePending([...pending, event])
  logDev(`app usage: ${event} queued`)
}

/** De apiClient kent geen timeout, dus een verzoek dat blijft hangen settelt
 *  nooit. Zonder deze race blijft `inFlight` de rest van het tabblad `true`
 *  staan en meldt dit tabblad nooit meer iets. Het verzoek zelf mag daarna
 *  gerust alsnog aankomen — we doen niets met het antwoord. */
const SEND_TIMEOUT_MS = 10_000

/** Welke bundel dit is. Zelfde waarde die AppSignal als revision meestuurt,
 *  zodat een melding en een incident aan elkaar te knopen zijn. */
function buildId(): string | undefined {
  return typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : undefined
}

async function send(event?: InstallEvent): Promise<void> {
  const build = buildId()
  const request = PatientService.reportAppUsage({
    displayMode: displayMode(),
    ...(build ? { buildId: build } : {}),
    ...(event ? { event } : {}),
  })

  let timer: ReturnType<typeof setTimeout> | undefined
  const bail = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error('app usage report timed out')), SEND_TIMEOUT_MS)
  })

  try {
    await Promise.race([request, bail])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** Twee mounts in dezelfde tick mogen niet twee keer melden. */
let inFlight = false

/** Bij uitloggen vergeten dat er al gemeld is. Anders valt op een gedeeld
 *  toestel de start van de volgende patient in hetzelfde tabblad weg. */
export function resetAppUsageSession(): void {
  try {
    session()?.removeItem(SESSION_KEY)
  } catch {
    /* niets te wissen dan */
  }
}

/** Melden hoe deze start eruitzag, plus wat er nog in de wachtrij stond.
 *  Fire-and-forget: een mislukking mag de patient nooit iets kosten. */
export async function reportAppLaunch(): Promise<void> {
  if (readStore(session(), SESSION_KEY) === 'true') return
  if (inFlight) return
  inFlight = true

  // finally, niet aan het eind van de functie: een onverwachte throw hier zou
  // de vlag anders voor de rest van het tabblad op true laten staan en elke
  // volgende melding onderdrukken.
  try {
    // Ook in browsermodus melden: die starts zijn de deler waartegen het
    // app-gebruik afgezet wordt.
    let reported = true
    try {
      await send()
    } catch {
      reported = false
      logDev('app usage: launch report failed')
    }

    const pending = readPending()
    if (pending.length > 0) {
      const remaining: InstallEvent[] = []
      for (const event of pending) {
        try {
          await send(event)
        } catch {
          remaining.push(event)
        }
      }
      writePending(remaining)
    }

    // Pas afvinken als de melding echt geland is. authStore rendert optimistisch
    // op een bewaarde hint, dus dit kan afvuren voordat de cookie gevalideerd is
    // -- en dan is een tweede poging na de echte login precies wat we willen.
    if (reported) writeStore(session(), SESSION_KEY, 'true')
  } finally {
    inFlight = false
  }
}
